import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { ArrowUpRight } from "lucide-react";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(email, password, name);
      navigate("/app");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[hsl(var(--bg))]">
      <div className="flex flex-col p-8 sm:p-16 justify-center order-2 lg:order-1">
        <Link to="/" className="flex items-center gap-3 mb-12" data-testid="register-logo">
          <div className="h-8 w-8 bg-[hsl(var(--primary))] grid place-items-center">
            <span className="text-white font-display text-lg leading-none">N</span>
          </div>
          <div className="font-display text-lg tracking-tight">NASHUKA</div>
        </Link>
        <div className="overline mb-3">Open account</div>
        <h1 className="font-display text-4xl leading-tight">Banking in 60 seconds.</h1>
        <p className="text-[hsl(var(--text-muted))] mt-3">
          We'll provision a checking account with a $100 welcome credit so you can move money instantly.
        </p>

        <form className="mt-10 max-w-md space-y-5" onSubmit={submit}>
          <div>
            <label className="overline block mb-2">Full name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-transparent border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none px-4 py-3"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none px-4 py-3"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none px-4 py-3"
              data-testid="register-password-input"
            />
          </div>
          {err && (
            <div className="text-sm text-[hsl(var(--destructive))]" data-testid="register-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[hsl(var(--primary))] text-white px-4 py-3 hover:bg-[hsl(var(--primary))]/90 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="register-submit"
          >
            {busy ? "Creating…" : "Create account"} <ArrowUpRight className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-10 text-sm text-[hsl(var(--text-muted))]">
          Already have an account?{" "}
          <Link to="/login" className="text-[hsl(var(--primary))] underline" data-testid="link-to-login">
            Sign in
          </Link>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img
          src="https://images.pexels.com/photos/3137072/pexels-photo-3137072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
          alt=""
          className="absolute inset-0 w-full h-full object-cover grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-[hsl(var(--accent))]/30 via-transparent to-[hsl(var(--primary))]/40" />
      </div>
    </div>
  );
};

export default Register;
